import React, {useEffect, useMemo, useState} from "react";
import {Alert, Box, Breadcrumbs, Chip, CircularProgress, Link, Paper, Stack, Tab, Tabs, Typography, useTheme} from "@mui/material";
import {LayoutDashboard, ShieldCheck} from "lucide-react";
import {Link as RouterLink, useNavigate, useParams} from "react-router-dom";
import {FeedbackSnackbar, PageHeader} from "../../shared/ui";
import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {
    cancelarLicenca,
    contratarOpcional,
    darDesconto,
    desfazerTroca,
    desistirDoCancelamento,
    listPlanosDoSistema,
    patchLicencaStatus,
    removerDesconto,
    retirarOpcional,
    trocarPlano,
    updateLicenca
} from "./LicencasService";
import {getStatusColor, getStatusLabel} from "./licencasUtils";
import {destinosDaTroca, efeitoDeTirarOpcional} from "./assinaturasUtils";
import {useAssinatura} from "./useAssinatura";
import VisaoGeralDaAssinatura from "./components/VisaoGeralDaAssinatura";
import DireitoDaAssinatura from "./components/DireitoDaAssinatura";
import SituacaoDialog from "./components/SituacaoDialog";
import {CancelamentoDialog, ConfirmarDialog, DescontoDialog, MeioDialog, TrocaDePlanoDialog} from "./components/AcoesDaAssinatura";

const ABAS = [
    {id: 'visao-geral', label: 'Visão geral'},
    {id: 'direito', label: 'Direito e opcionais'},
];

const mensagemDaApi = (response, padrao) => response?.data?.message || padrao;

/*
 * Página da assinatura (BBF-147, protótipo v2 "Assinaturas"): Visão geral e Direito e opcionais. As abas Perfis e
 * Usuários entram na fase 3, e Uso na fase 5.
 */
export default function AssinaturaPage(props) {
    const {id} = useParams();
    return <PaginaDaAssinatura key={id} {...props} />;
}

function PaginaDaAssinatura({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const {id, aba: abaDaUrl} = useParams();
    const aba = ABAS.some((item) => item.id === abaDaUrl) ? abaDaUrl : 'visao-geral';
    const [open, setOpen] = useState(openParent);
    const dados = useAssinatura(id);
    const {carregando, erro, licenca, historico, empresa, versao, opcionais, modulos, previa, recarregar, ajustarLicenca} = dados;

    const [planos, setPlanos] = useState({carregando: false, itens: null});
    const [dialogo, setDialogo] = useState(null);
    const [ocupado, setOcupado] = useState(false);
    const [erroDoDialogo, setErroDoDialogo] = useState('');
    const [feedback, setFeedback] = useState({error: '', success: ''});

    const sistemaId = licenca?.plano?.id_sistema || licenca?.plano?.sistema?.id;
    useEffect(() => {
        if (!sistemaId) return undefined;
        let ativo = true;
        setPlanos({carregando: true, itens: null});
        listPlanosDoSistema(sistemaId)
            .then((response) => ativo && setPlanos({carregando: false, itens: response?.data?.valor?.items || []}))
            .catch(() => ativo && setPlanos({carregando: false, itens: []}));
        return () => {
            ativo = false;
        };
    }, [sistemaId]);

    const destinos = useMemo(() => destinosDaTroca(licenca, planos.itens || []), [licenca, planos.itens]);
    const versaoAVenda = useMemo(() => {
        const plano = (planos.itens || []).find((item) => item.id === licenca?.id_plano);
        return plano?.versao_a_venda ? {...plano.versao_a_venda, id_plano: plano.id} : null;
    }, [licenca?.id_plano, planos.itens]);
    const nomesDeModulo = useMemo(() => Object.fromEntries([
        ...modulos.map((modulo) => [modulo.id, modulo.nome]),
        ...(opcionais?.oferecidos || []).map((item) => [item.id_modulo, item.nome]),
    ]), [modulos, opcionais]);

    const abrir = (tipo, extra = {}) => {
        setErroDoDialogo('');
        setDialogo({tipo, ...extra});
    };
    const fechar = () => (ocupado ? null : setDialogo(null));

    // Toda ação: chama a API, mostra a mensagem dela (que diz o que aconteceu de fato) e relê a assinatura.
    // `jaGarantido` aplica na hora o que a ação garante: a releitura busca sete coisas em cinco serviços e, com
    // algum deles frio, leva segundos, com o aviso dizendo que mudou e a tela ainda mostrando o antes.
    const executar = async (acao, padrao, jaGarantido) => {
        setOcupado(true);
        setErroDoDialogo('');
        try {
            const response = await acao();
            if (jaGarantido) ajustarLicenca(jaGarantido);
            setDialogo(null);
            setFeedback({error: '', success: mensagemDaApi(response, padrao)});
            await recarregar();
        } catch (error) {
            setErroDoDialogo(getBackendErrorMessage(error, 'Não foi possível concluir. Nada mudou.'));
        } finally {
            setOcupado(false);
        }
    };

    const acoes = {
        cancelar: () => abrir('cancelar'),
        desistir: () => abrir('desistir'),
        trocar: (alvoInicial) => abrir('trocar', {alvoInicial}),
        desfazerTroca: () => abrir('desfazer-troca'),
        desconto: () => abrir('desconto'),
        removerDesconto: () => abrir('remover-desconto'),
        trocarMeio: () => abrir('meio'),
        mudarSituacao: () => abrir('situacao'),
    };

    const menus = [
        {label: "Dashboard", id: "dashboard", selected: false, disabled: false, icon: <LayoutDashboard size={20} />, onClick: () => navigate("/licencas/dashboard")},
        {label: "Licenças", id: "licencas", selected: true, disabled: false, icon: <ShieldCheck size={20} />, onClick: () => navigate("/licencas/gestao")},
    ];

    let conteudo = null;
    if (carregando && !licenca) {
        conteudo = (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{py: 6}}>
                <CircularProgress size={20} />
                <Typography color="text.secondary">Carregando a assinatura…</Typography>
            </Stack>
        );
    } else if (erro && !licenca) {
        conteudo = <Alert severity="error">{erro}</Alert>;
    } else if (licenca) {
        const empresaNome = licenca.empresa?.nome_fantasia || licenca.empresa?.razao_social || 'Empresa';
        conteudo = (
            <Stack spacing={3}>
                <Breadcrumbs>
                    <Link component={RouterLink} to="/licencas/gestao" underline="hover" color="inherit">Assinaturas</Link>
                    <Typography color="text.primary">{empresaNome}</Typography>
                </Breadcrumbs>
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="h4" component="h1" fontWeight={700} sx={{overflowWrap: 'anywhere'}}>{empresaNome}</Typography>
                        <Chip label={getStatusLabel(licenca.status)} color={getStatusColor(licenca.status)} />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {licenca.versao?.nome_plano || licenca.plano?.nome_plano} v{licenca.versao?.numero} · {licenca.plano?.sistema?.nome}
                        {' · '}<Box component="span" sx={{fontFamily: 'monospace'}}>{licenca.tenant_id}</Box>
                    </Typography>
                </Box>
                <Paper variant="outlined" sx={{px: 1}}>
                    <Tabs value={aba} onChange={(_, valor) => navigate(`/licencas/${licenca.id}/${valor}`)} aria-label="Partes da assinatura">
                        {ABAS.map((item) => <Tab key={item.id} value={item.id} label={item.label} sx={{textTransform: 'none'}} />)}
                    </Tabs>
                </Paper>
                {aba === 'visao-geral' ? (
                    <VisaoGeralDaAssinatura
                        licenca={licenca}
                        empresa={empresa}
                        versaoDetalhe={versao}
                        versaoAVenda={versaoAVenda}
                        historico={historico}
                        nomesDeModulo={nomesDeModulo}
                        previa={previa}
                        acoes={acoes}
                    />
                ) : (
                    <DireitoDaAssinatura
                        licenca={licenca}
                        versaoDetalhe={versao}
                        opcionais={opcionais}
                        modulos={modulos}
                        ocupado={ocupado}
                        onContratar={(modulo, manter) => abrir('contratar', {modulo, manter})}
                        onRetirar={(modulo) => abrir('retirar', {modulo})}
                    />
                )}
            </Stack>
        );
    }

    const precoDoOpcional = (modulo) => (versao?.modulos || []).find((item) => item.id_modulo === modulo?.id)?.preco_opcional;
    const reais = (valor) => new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(Number(valor) || 0);

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <PageHeader title="Gestão de Licenças" titleAction={() => navigate("/")} itemsMenu={menus} onHandleMenuOpen={() => null} onHandleDrawerToggle={setOpen} openParent={open} />
            <Box component="main" sx={{flexGrow: 1, p: {xs: 2, md: 4}, minWidth: 0, boxSizing: 'border-box', mt: 8, backgroundColor: theme.palette.background.default, minHeight: 'calc(100dvh - 64px)'}}>
                {conteudo}
            </Box>

            {licenca ? (
                <>
                    <CancelamentoDialog aberto={dialogo?.tipo === 'cancelar'} licenca={licenca} diaFechamento={empresa?.dia_fechamento} ocupado={ocupado} erro={erroDoDialogo}
                                        onFechar={fechar} onConfirmar={(motivo) => executar(() => cancelarLicenca(licenca.id, motivo), 'Cancelamento registrado.')} />
                    <ConfirmarDialog aberto={dialogo?.tipo === 'desistir'} titulo="Desistir do cancelamento?" rotulo="Desistir do cancelamento"
                                     texto="A assinatura continua como está, e nada muda para a empresa." ocupado={ocupado} erro={erroDoDialogo}
                                     onFechar={fechar} onConfirmar={() => executar(() => desistirDoCancelamento(licenca.id), 'Cancelamento desfeito.')} />
                    <TrocaDePlanoDialog aberto={dialogo?.tipo === 'trocar'} licenca={licenca} destinos={destinos} carregando={planos.carregando}
                                        diaFechamento={empresa?.dia_fechamento} alvoInicial={dialogo?.alvoInicial} ocupado={ocupado} erro={erroDoDialogo}
                                        onFechar={fechar} onConfirmar={(alvo) => executar(() => trocarPlano(licenca.id, alvo.id), 'Troca de plano registrada.')} />
                    <ConfirmarDialog aberto={dialogo?.tipo === 'desfazer-troca'} titulo="Desfazer a troca de plano?" rotulo="Desfazer a troca"
                                     texto="A assinatura continua no plano de hoje, e a troca agendada sai." ocupado={ocupado} erro={erroDoDialogo}
                                     onFechar={fechar} onConfirmar={() => executar(() => desfazerTroca(licenca.id), 'Troca de plano desfeita.')} />
                    <DescontoDialog aberto={dialogo?.tipo === 'desconto'} licenca={licenca} ocupado={ocupado} erro={erroDoDialogo}
                                    onFechar={fechar} onConfirmar={(desconto) => executar(() => darDesconto(licenca.id, desconto), 'Desconto salvo.')} />
                    <ConfirmarDialog aberto={dialogo?.tipo === 'remover-desconto'} titulo="Remover o desconto?" rotulo="Remover" perigo
                                     texto="Vale a partir da próxima fatura: a mensalidade volta ao preço da versão." ocupado={ocupado} erro={erroDoDialogo}
                                     onFechar={fechar} onConfirmar={() => executar(() => removerDesconto(licenca.id), 'Desconto removido.')} />
                    <MeioDialog aberto={dialogo?.tipo === 'meio'} licenca={licenca} ocupado={ocupado} erro={erroDoDialogo} onFechar={fechar}
                                onConfirmar={(meio) => executar(() => updateLicenca(licenca.id, {
                                    id_empresa: licenca.id_empresa,
                                    id_plano: licenca.id_plano,
                                    chave_ativacao: licenca.chave_ativacao,
                                    validade_inicio: licenca.validade_inicio,
                                    meio_pagamento: meio,
                                }), 'Meio de pagamento trocado.')} />
                    <SituacaoDialog open={dialogo?.tipo === 'situacao'} licenca={licenca} saving={ocupado} onClose={fechar} erro={erroDoDialogo}
                                    onConfirm={(para, motivo) => executar(() => patchLicencaStatus(licenca.id, para, motivo), `Situação alterada para ${getStatusLabel(para)}.`,
                                        (atual) => ({...atual, status: para}))} />
                    <ConfirmarDialog aberto={dialogo?.tipo === 'contratar'} titulo={dialogo?.manter ? `Manter ${dialogo?.modulo?.nome}?` : `Contratar ${dialogo?.modulo?.nome}?`}
                                     rotulo={dialogo?.manter ? 'Manter o opcional' : 'Contratar'} ocupado={ocupado} erro={erroDoDialogo}
                                     texto={dialogo?.manter
                                         ? 'A saída agendada é desfeita, e o opcional continua contratado.'
                                         : `O acesso vale agora, e ${reais(precoDoOpcional(dialogo?.modulo))} por mês entram na próxima fatura, sem pró-rata.`}
                                     onFechar={fechar} onConfirmar={() => executar(() => contratarOpcional(licenca.id, dialogo.modulo.id), 'Opcional contratado.')} />
                    <ConfirmarDialog aberto={dialogo?.tipo === 'retirar'} titulo={`Tirar ${dialogo?.modulo?.nome}?`} rotulo="Tirar o opcional" perigo
                                     texto={efeitoDeTirarOpcional(licenca, empresa?.dia_fechamento)} ocupado={ocupado} erro={erroDoDialogo}
                                     onFechar={fechar} onConfirmar={() => executar(() => retirarOpcional(licenca.id, dialogo.modulo.id), 'Saída do opcional registrada.')} />
                </>
            ) : null}
            <FeedbackSnackbar feedback={feedback} onClose={() => setFeedback({error: '', success: ''})} />
        </Box>
    );
}
